import { Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { AddDomainRequest, CNameConfiguration, EmailDnsStatusHTMLTagSpan, EmailDnsValidLabel } from '@models/email/emailDomain';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { EmailCampaignDnsService } from '@services/email-campaign-dns.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { EmailDnsVerifyConfigModalComponent } from '@ui/components/modal/email-dns-vconfig-modal/email-dns-verify-config-modal.component';
import { flattenObject } from '@utils/utils';
import { ToastContainerDirective } from 'ngx-toastr';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';
@Component({
  selector: 'wsb-email-campaigns-tab-dns',
  templateUrl: './email-campaign-dns.component.html',
  styleUrls: ['./email-campaign-dns.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class EmailCampaignDnsComponent implements OnInit {
  @ViewChild(AtlasGridComponent) grid: AtlasGridComponent;
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;
  @ViewChild('modalDelete') modalDelete;
  @ViewChild('modalDnsViewConfiguration') modalDnsViewConfiguration: EmailDnsVerifyConfigModalComponent;
  @ViewChild('modalAddNewDns') modalAddNewDns;

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  tableColumnDefs = null;
  tableGridOptions = null;
  tableRowData = null;

  modalRefDelete: NgbModalRef;
  modalRefDnsViewConfiguration: NgbModalRef;
  modalRefAddNewDns: NgbModalRef;

  // input modals
  textDnsDelete;
  idDomainViewConfig;
  cNameNewDomain: CNameConfiguration;
  gridAnimationState = 'hide';
  private idDomainToDelete;
  private stringEmpty = '';

  constructor(
    private translateService: TranslateService,
    private emailDnsService: EmailCampaignDnsService,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private atlasToast: AtlasToastService
  ) {}

  ngOnInit(): void {
    this.createTable();
    this.gridOptionDefinition();
  }

  private gridOptionDefinition() {
    // const that = this;
    this.tableGridOptions = AtlasGridOptions.deserialize({
      pagination: true,
      disablePagination: true,
      rowHeight: 80
    });
    this.reloadList();
  }

  private reloadList() {
    this.loaderService.show();
    this.emailDnsService
      .getListDomainCampaigns()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(data => {
        if (data) {
          this.tableRowData = data.map(d => flattenObject(d));
        }
        this.gridAnimationState = 'show';
      });
  }

  private getHtmlDnsRender(data) {
    const cNameConf: CNameConfiguration = CNameConfiguration.deserialize(data);
    const dns = this.getDns(cNameConf);
    const tagSpan = '<span><b>' + dns + '</b></span>';
    return '<div class ="rendering-title">' + tagSpan + '</div>';
  }

  private getDns(cNameConf: CNameConfiguration): string {
    const dot = '.';
    const subDomain = cNameConf.subdomain ? cNameConf.subdomain : this.stringEmpty;
    const domain = cNameConf.domain ? cNameConf.domain : this.stringEmpty;
    return subDomain + dot + domain;
  }

  private createTable() {
    this.tableColumnDefs = [
      {
        headerNameTranslate: 'emaildns_full_domain',
        field: 'domain',
        sortable: false,
        suppressCellFocus: true,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const data = params.data;
              return this.getHtmlDnsRender(data);
            }
            return this.stringEmpty;
          }
        },
        maxWidth: 460
      },
      {
        headerNameTranslate: 'common_status',
        field: 'valid',
        sortable: false,
        suppressCellFocus: true,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const data = params.data;
              return this.getHtmlRenderStatus(data);
            }
            return this.stringEmpty;
          }
        },
        maxWidth: 460
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        resizable: false,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              label: 'emaildns_verify',
              hide: dns => (dns?.valid === true ? true : false),
              onClick: e => this.pressVerifyDnsButton(e)
            },
            {
              label: 'emaildns_view_config',
              onClick: e => this.openModalDnsViewConfiguration(e)
            },
            {
              label: 'common_delete',
              onClick: e => this.openModalDeleteDns(e)
            }
          ]
        },
        cellClass: ['button-group', 'text-end'],
        maxWidth: 82
      }
    ];
  }

  private getHtmlRenderStatus(data) {
    const cNameConf: CNameConfiguration = CNameConfiguration.deserialize(data);
    return this.getHtmlTagByValidStatus(cNameConf.valid);
  }

  private getHtmlTagByValidStatus(valid: boolean) {
    if (valid === true) {
      const validLabel = this.getTranslateLabels(EmailDnsValidLabel.VERIFY_LABEL);
      return EmailDnsStatusHTMLTagSpan.VERIFY + validLabel + '</span>';
    } else if (valid === false) {
      const validLabel = this.getTranslateLabels(EmailDnsValidLabel.PENDING_LABEL);
      return EmailDnsStatusHTMLTagSpan.PENDING + validLabel + '</span>';
    }
    // valid is undefine
    return this.stringEmpty;
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  private setTextMessageDelete(cNameConf: CNameConfiguration) {
    const dns = this.getDns(cNameConf);
    this.textDnsDelete = ': ' + dns;
  }

  private openModalDeleteDns(data?: any): void {
    const cNameConf: CNameConfiguration = CNameConfiguration.deserialize(data);
    this.setTextMessageDelete(cNameConf);
    this.idDomainToDelete = cNameConf.id;
    this.modalRefDelete = this.modalService.open(this.modalDelete);
    this.modalRefDelete.result.then(
      result => {},
      reason => {}
    );
  }

  modalDeleteDns() {
    this.callApiDeleteDns(this.idDomainToDelete);
    this.modalRefDelete.close();
  }

  modalDeleteDnsDismiss() {
    this.modalRefDelete.close();
    this.grid.refresh();
  }

  private callApiDeleteDns(idDomainToDelete: number) {
    this.emailDnsService.deleteDomainCampaign(idDomainToDelete).subscribe(
      success => {
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
        this.reloadList();
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        this.reloadList();
      }
    );
  }

  openModalDnsViewConfiguration(data?: any) {
    const cNameConf: CNameConfiguration = CNameConfiguration.deserialize(data);
    this.idDomainViewConfig = cNameConf.id;
    this.modalRefDnsViewConfiguration = this.modalService.open(this.modalDnsViewConfiguration, { size: 'xl' });
    this.modalRefDnsViewConfiguration.result.then(
      result => {},
      reason => {}
    );
  }

  openModalDnsViewConfigurationByAddDomain(cNameConf: CNameConfiguration) {
    this.modalRefDnsViewConfiguration = this.modalService.open(EmailDnsVerifyConfigModalComponent, { size: 'xl' });
    this.modalRefDnsViewConfiguration.componentInstance.cNameConfigByAddNewDomain = cNameConf;
    this.modalRefDnsViewConfiguration.componentInstance.cancel.subscribe(() => this.modalDnsViewConfigurationDismiss());
    this.modalRefDnsViewConfiguration.result.then(
      result => {},
      reason => {}
    );
  }

  modalDnsViewConfigurationDismiss() {
    this.modalRefDnsViewConfiguration.close();
    this.reloadList();
  }

  private pressVerifyDnsButton(data: any) {
    const cNameConf: CNameConfiguration = CNameConfiguration.deserialize(data);
    this.callApiVerifyDns(cNameConf.id);
  }

  private callApiVerifyDns(idDomain: number) {
    this.emailDnsService.verifyDnsCampaign(idDomain).subscribe(
      success => {
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
        this.reloadList();
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        this.reloadList();
      }
    );
  }

  openModalAddDns() {
    this.modalRefAddNewDns = this.modalService.open(this.modalAddNewDns);
    this.modalRefAddNewDns.result.then(
      result => {},
      reason => {}
    );
  }

  modalAddDns(domain: any) {
    if (domain) {
      const request: AddDomainRequest = new AddDomainRequest();
      request.domain = this.deletePrefixWWWFromDomain(domain.domain); // domain.domain;
      this.callApiAddNewDomain(request);
    }
  }

  modalAddDnsCancel() {
    this.modalRefAddNewDns.close();
    this.reloadList();
  }

  private callApiAddNewDomain(request: AddDomainRequest) {
    this.emailDnsService.addNewDomainCampaign(request).subscribe(
      newDomain => {
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
        this.modalRefAddNewDns.close();
        this.openModalDnsViewConfigurationByAddDomain(newDomain);
        this.reloadList();
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        this.modalRefAddNewDns.close();
        this.reloadList();
      }
    );
  }

  private deletePrefixWWWFromDomain(domain: string) {
    if (domain.startsWith('www.')) {
      return domain.split('www.')[1];
    }
    return domain;
  }
}
